/**
 * Pure, framework-free predicates for "does this class post still owe the
 * viewer's own acknowledgement", and (classPostEditLockedByAck, shared with
 * netlify/functions/classPostRecord.mjs) "may its words still change" - split out of classPosts.ts, which
 * re-exports both names unchanged, so nothing importing them from there
 * has to change. classPosts.ts itself pulls in firebase/firestore and
 * ./firebase at module scope, which crashes a plain `node
 * --experimental-strip-types` test with no bundler and no Firebase
 * environment (confirmed by trying it: "Cannot find module '.../firebase'").
 * familyWaitingOnYouCore.ts (D4 P1-2 round three,
 * docs/audits/refute-2026-09-16/REFUTE_msg2_R2.md) needs the SAME predicate
 * the home card's AckMessageCard uses, kept in one place so the two can
 * never drift apart, and it needs to stay importable the same
 * framework-free way unreadHomeThreadsCore.ts, staffWaitingOnYouCore.ts and
 * familyWaitingOnYouCore.ts already are.
 */

/** Just the ClassPostDoc fields these predicates read. A structural type
 *  instead of importing ClassPostDoc itself, so this file needs no import
 *  from classPosts.ts at all, in either direction. Read-only, so the Heads
 *  up card's own post shape (lib/headsUp.ts HeadsUpClassPost) fits it too. */
export interface AckPredicatePost {
  ackRequired?: boolean;
  authorUid: string;
  expectedRecipientUids?: readonly string[];
  backfilledRecipientUids?: readonly string[];
  acks?: Readonly<Record<string, unknown>>;
  dismissedBy?: Readonly<Record<string, unknown>>;
  reactions?: Readonly<Record<string, unknown>>;
}

/**
 * WAS THIS VIEWER ADDED TO THIS POST AFTER IT WAS WRITTEN?
 *
 * The enrollment repair (netlify/functions/setEnrollment.mjs) adds a newly
 * linked guardian back to the recent class posts she should be able to read,
 * because the audience is frozen at create time and nothing used to add
 * anyone back. Bobbie, 2026-09-01: "They should also not have to acknowledge
 * old posts." So a backfilled recipient READS the post and owes nothing on
 * it: no acknowledgement is asked of her, no badge counts her, and no staff
 * report lists her among the people who have not answered.
 *
 * True only for a post that carries her in the backfilled list. Her own acks
 * and dismissals are untouched, so a post she was a real recipient of behaves
 * exactly as it always did.
 */
export function classPostAckExempt(post: Pick<AckPredicatePost, 'backfilledRecipientUids'>, meUid: string): boolean {
  return (post.backfilledRecipientUids ?? []).includes(meUid);
}

/**
 * Does this post still owe the viewer's OWN acknowledgement? The class page's
 * Waiting on you pill and Understood/complete control, the post's place in
 * Current or Past there, the staff home's Got it card, and the family home's
 * Heads up line ("A post from ... needs your acknowledgement", lib/headsUp.ts)
 * all ask this.
 *
 * Her acknowledgement, her dismissal, or her OWN emoji answers it. The emoji
 * counts on its own, with or without the acks stamp the reacting surfaces now
 * write beside it (Bobbie, 2026-10-25, decision card, her pick "Count as
 * acknowledged (Recommended)"): a family who tapped an emoji on a must-read
 * before an emoji also stamped the ack HAS acknowledged it, following her
 * 2026-08-18 rule that Done, Acknowledge and a reaction are equivalent and
 * "the same tap must never be demanded twice". A dancer's emoji lives in
 * dancerReactions and is never read here.
 *
 * ONE answer with the home count. Live family report GPyz1G4C (Bobbie
 * confirmed it by screenshot) was this page and the home answering
 * differently: the home kept nagging while the page took the emoji as done,
 * so the green cards were permanent. The home count now asks
 * classPostOwesMyAck (lib/messagesUnreadCore.ts, B17), which takes her emoji
 * as her answer, so this asks the same question for one sign-in, and
 * tests/class-post-ack-revisit.test.mjs holds the two equal over every mix
 * of marks.
 */
export function classPostAckPending(post: AckPredicatePost, meUid: string): boolean {
  return (
    !!post.ackRequired &&
    post.authorUid !== meUid &&
    (post.expectedRecipientUids ?? []).includes(meUid) &&
    !classPostAckExempt(post, meUid) &&
    !post.acks?.[meUid] &&
    !post.dismissedBy?.[meUid] &&
    !post.reactions?.[meUid]
  );
}

/**
 * Is the post still waiting on this one recipient, for a staff chase list?
 * Nothing from her yet: no ack, no dismissal, and no emoji of her own. Her
 * emoji is her answer, stamped or not (Bobbie, 2026-10-25, "Count as
 * acknowledged"), the same reading as the class roster's Still needed
 * (pages/classPage/helpers.ts postAckState). Who the post asked is the
 * caller's list; this only reads her marks. The teacher's own Heads up line
 * ("Nobody has acknowledged your post", lib/headsUp.ts) asks it of every
 * family the post asked.
 */
export function classPostAnswerStillNeeded(
  post: Pick<AckPredicatePost, 'acks' | 'dismissedBy' | 'reactions'>,
  uid: string
): boolean {
  return !post.acks?.[uid] && !post.dismissedBy?.[uid] && !post.reactions?.[uid];
}

/** Her own answer to one class post, on one sign-in, as a reading screen shows it. */
export interface ClassPostMyAnswer {
  /** The post still asks her: Waiting on you, Understood/complete and Dismiss. */
  owed: boolean;
  /** Show her as having acknowledged it (Understood, You said got it). */
  acknowledged: boolean;
  /** She dismissed it. */
  dismissed: boolean;
}

/**
 * Her answer to a class post, for a screen that reads one post at a time
 * (her dancer's page). `owed` is classPostAckPending, the same question the
 * home count and the class page ask. She shows as acknowledged for an ack,
 * or for her own emoji unless she dismissed the post, the order the staff
 * roster keeps (pages/classPage/helpers.ts postAckState): her emoji is her
 * acknowledgement, stamped or not (Bobbie, 2026-10-25, "Count as
 * acknowledged").
 */
export function classPostMyAnswer(post: AckPredicatePost, meUid: string | null | undefined): ClassPostMyAnswer {
  if (!meUid) return { owed: false, acknowledged: false, dismissed: false };
  const dismissed = Boolean(post.dismissedBy?.[meUid]);
  return {
    owed: classPostAckPending(post, meUid),
    acknowledged: Boolean(post.acks?.[meUid]) || (Boolean(post.reactions?.[meUid]) && !dismissed),
    dismissed,
  };
}

/** Just the fields the edit lock reads. Loose on purpose: the server hands
 *  it a raw Firestore row, so every field is checked before it is trusted. */
export interface AckEditLockPost {
  ackRequired?: unknown;
  expectedRecipientUids?: unknown;
  originalRecipientUids?: unknown;
  backfilledRecipientUids?: unknown;
  acks?: unknown;
  dismissedBy?: unknown;
  reactions?: unknown;
}

function uidList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((uid): uid is string => typeof uid === 'string' && uid.length > 0)
    : [];
}

function stampedBy(map: unknown, uid: string): boolean {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return false;
  return Boolean((map as Record<string, unknown>)[uid]);
}

/**
 * HAS ANYBODY ACTED ON THIS ACK-REQUIRED POST YET? Then its words are locked.
 *
 * The rule the class page has always shown ("An ack-required post can only be
 * edited before anybody has acted on it, otherwise the wording people agreed
 * to would change under them"), spelled ONCE so the screen and the server
 * answer it the same way. Until 2026-10-20 it lived only in PostCard.tsx, so
 * the server (netlify/functions/classPostRecord.mjs) would reword a notice a
 * family had already said "got it" to. Both now call this.
 *
 * "Acted" is exactly the roster's answer (postAckState in
 * pages/classPage/helpers.ts): one of the people the post ASKED (its
 * recipients, or the original recipients of a taken-back post, minus anyone
 * the enrollment repair added later) acknowledged it, reacted to it, or
 * dismissed it. A dancer's own reaction lives in dancerReactions and never
 * counts, the same as everywhere else. Delete is never locked by this.
 */
export function classPostEditLockedByAck(post: AckEditLockPost): boolean {
  if (!post.ackRequired) return false;
  const expected = uidList(post.expectedRecipientUids);
  const recipients = expected.length > 0 ? expected : uidList(post.originalRecipientUids);
  const exempt = uidList(post.backfilledRecipientUids);
  return recipients
    .filter((uid) => !exempt.includes(uid))
    .some((uid) => stampedBy(post.acks, uid)
      || stampedBy(post.reactions, uid)
      || stampedBy(post.dismissedBy, uid));
}
